import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  InstagramOutlined,
  FacebookFilled,
  LinkedinFilled,
  WhatsAppOutlined,
  YoutubeFilled,
} from '@ant-design/icons';
import ExternalLink from 'components/ExternalLink/ExternalLink';
import Newsletter from '../Newsletter/Newsletter';

const islamicMonths = {
  '01': 'Muharram',
  '02': 'Safar',
  '03': 'Rabi al-Awwal',
  '04': 'Rabi al-Thani',
  '05': 'Jumada al-Awwal',
  '06': 'Jumada al-Thani',
  '07': 'Rajab',
  '08': "Sha'ban",
  '09': 'Ramadan',
  '10': 'Shawwal',
  '11': "Dhuʻl-Qiʻdah",
  '12': 'Dhu al-Hijjah',
};

const getIslamicDate = () => {
  const today = new Date();

  const gregorian = today.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });

  const hijriParts = new Intl.DateTimeFormat('en-SA-u-ca-islamic-umalqura', {
    day: 'numeric',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(today);

  const day = hijriParts.find((p) => p.type === 'day')?.value;
  const month = hijriParts.find((p) => p.type === 'month')?.value;
  const year = hijriParts.find((p) => p.type === 'year')?.value;

  return {
    gregorian,
    hijri: `${day} ${islamicMonths[month] || month} ${year}`,
  };
};

const footerColumns = [
  {
    title: 'commonsplace',
    links: [
      { label: 'Startseite', href: '/' },
      { label: 'Projekte', href: '/projects' },
      { label: 'Events', href: '/events' },
      { label: 'Registrieren', to: '/login#!' },
      { label: 'Anmelden', to: '/login' },
    ],
  },
  {
    title: 'Hilfe',
    links: [
      { label: 'Wie funktioniert es?', to: '/how-it-works' },
      { label: 'Sicherheit', to: '/transparency-and-trust' },
      { label: 'Richtlinien', to: '/guidelines' },
      { label: 'FAQ', to: '/faqs' },
      { label: 'Über uns', to: '/about-us' },
      { label: 'Kontakt', to: '/contact-us' },
      { label: 'Gebühren', to: '/fees' },
    ],
  },
  {
    title: 'Community',
    links: [
      {
        label: 'Blog',
        href: 'https://blog.commonsplace.de/',
        openInNewTab: true,
      },
      {
        label: 'Podcast',
        href: 'https://podcast.commonsplace.de/',
        badge: 'Neu',
        openInNewTab: true,
      },
      {
        label: 'MuslimLab',
        href: 'https://muslimlab.de/',
        openInNewTab: true,
      },
      {
        label: 'Mach mit!',
        href: 'https://docs.google.com/forms/d/e/1FAIpQLSfvnfznI_ZGrnlIbnNkgULmBrgLoUEH4BNwSQV1ZCmMkFo6_w/viewform?usp=dialog',
        openInNewTab: true,
      },
    ],
  },
  {
    title: 'Rechtliches',
    links: [
      { label: 'Impressum', to: '/impressum' },
      { label: 'Datenschutz', to: '/datenschutz' },
      { label: 'ANB', to: '/anb' },
    ],
  },
];

const Footer = () => {
  const dates = useMemo(() => getIslamicDate(), []);

  return (
    <>
      <Newsletter />
      <FooterWrapper>
        <TopContainer>
          <TopContent>
            <QuoteBlock>
              <Quote>
                “Der Beste unter den Menschen ist derjenige, der seinen
                Mitmenschen am nützlichsten ist.“
              </Quote>
              <QuoteAuthor>– Prophet Muhammed ﷺ</QuoteAuthor>
            </QuoteBlock>

            <LogoBlock>
              <ExternalLink href="/">
                <LogoImage
                  src={
                    'https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677e956903bbb028e9d12638_b3f8733e19dbacc0014314b036af41fe_logo-p-500.png'
                  }
                  alt="commonsplace logo"
                />
              </ExternalLink>

              <DateText>
                {dates.gregorian}
                <br />
                {dates.hijri}
              </DateText>
            </LogoBlock>
          </TopContent>
        </TopContainer>

        <MiddleContainer>
          <LinksGrid>
            {footerColumns.map((column) => (
              <FooterColumn key={column.title}>
                <ColumnTitle>{column.title}</ColumnTitle>

                <ColumnLinks>
                  {column.links.map((item) => (
                    <FooterLinkRow key={item.label}>
                      {item.to ? (
                        <FooterLink as={Link} to={item.to}>
                          {item.label}
                        </FooterLink>
                      ) : (
                        <FooterLink
                          as="a"
                          href={item.href}
                          target={item.openInNewTab ? '_blank' : undefined}
                          rel={item.openInNewTab ? 'noreferrer' : undefined}
                        >
                          {item.label}
                        </FooterLink>
                      )}

                      {item.badge ? <Badge>{item.badge}</Badge> : null}
                    </FooterLinkRow>
                  ))}
                </ColumnLinks>
              </FooterColumn>
            ))}

            <FooterColumn>
              <ColumnTitle>Bezahlmethoden</ColumnTitle>
              <PaymentGrid>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb07692b81fcdb6bc16a4_Payment%20method%20icon.svg"
                    loading="lazy"
                    alt="Visa"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb076e673cd519617f7ed_Payment%20method%20icon-1.svg"
                    loading="lazy"
                    alt="Mastercard"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb07649ace90fc02498b1_Payment%20method%20icon-2.svg"
                    loading="lazy"
                    alt="Maestro"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb076d3b07ac2a648d8b0_Payment%20method%20icon-3.svg"
                    loading="lazy"
                    alt="American Express"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb07678f8c4107f9e7a60_Payment%20method%20icon-4.svg"
                    loading="lazy"
                    alt="Giropay"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb0765fba5bc1df761f4f_Payment%20method%20icon-5.svg"
                    loading="lazy"
                    alt="Sepa"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb078e50e9390b999bee5_Payment%20method%20icon-6.svg"
                    loading="lazy"
                    alt="Ideal"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb076b16c668dc8b2c28b_Payment%20method%20icon-7.svg"
                    loading="lazy"
                    alt="Klarna"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb07621e51344733fab9a_Payment%20method%20icon-8.svg"
                    loading="lazy"
                    alt="Google Pay"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb0763fd4959ea6f070c7_Payment%20method%20icon-9.svg"
                    loading="lazy"
                    alt="Apple Pay"
                  />
                </PaymentItem>
                <PaymentItem>
                  <img
                    src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677fb076ba949fad55829d13_Payment%20method%20icon-10.svg"
                    loading="lazy"
                    alt="PayPal"
                  />
                </PaymentItem>
              </PaymentGrid>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Folge uns auf</ColumnTitle>
              <SocialRow>
                <SocialLink
                  href="https://www.instagram.com/commonsplace_de/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                >
                  <InstagramOutlined />
                </SocialLink>
                <SocialLink
                  href="https://www.facebook.com/commonsplace.de"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                >
                  <FacebookFilled />
                </SocialLink>
                <SocialLink
                  href="https://www.youtube.com/channel/UCDZ5ZtMl_l7Vm0ynrTp6sVg"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="YouTube"
                >
                  <YoutubeFilled />
                </SocialLink>
                <SocialLink
                  href="https://www.linkedin.com/company/cp21"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                >
                  <LinkedinFilled />
                </SocialLink>
                <SocialLink
                  href="https://whatsapp.com/channel/0029VaMMAq50QealT9ayL51r"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                >
                  <WhatsAppOutlined />
                </SocialLink>
              </SocialRow>
            </FooterColumn>
          </LinksGrid>
        </MiddleContainer>

        <BottomContainer>
          <BottomContent>
            <Copyright>© 2026 commonsplace. Alle Rechte vorbehalten.</Copyright>
          </BottomContent>
        </BottomContainer>
      </FooterWrapper>
    </>
  );
};

export default Footer;

const FooterWrapper = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 0 48px;
  gap: 64px;
  width: 100%;
  background: #f5fbee;

  @media (max-width: 1024px) {
    gap: 48px;
    padding: 56px 0 40px;
  }
`;

const BaseContainer = styled.div`
  width: 100%;
  max-width: 1280px;
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const TopContainer = styled(BaseContainer)``;
const MiddleContainer = styled(BaseContainer)``;
const BottomContainer = styled(BaseContainer)``;

const TopContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 48px 32px;
  padding: 0 0 64px;
  border-bottom: 1px solid #e9eaeb;

  @media (max-width: 900px) {
    padding-bottom: 40px;
  }
`;

const QuoteBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 480px;
  max-width: 768px;

  @media (max-width: 900px) {
    min-width: 100%;
  }
`;

const Quote = styled.h3`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #181d27;

  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 30px;
  }
`;

const QuoteAuthor = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #535862;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  width: 320px;
  min-width: 320px;

  @media (max-width: 900px) {
    width: 100%;
    min-width: 100%;
    align-items: flex-start;
  }
`;

const LogoImage = styled.img`
  width: 148px;
  height: auto;
  display: block;
`;

const DateText = styled.div`
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  color: #535862;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(96px, 1fr));
  gap: 32px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 28px 20px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ColumnTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #717680;
`;

const ColumnLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterLink = styled.a`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #535862;
  text-decoration: none;

  &:hover {
    color: #181d27;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  height: 22px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #414651;
`;

const PaymentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 172px;
`;

const PaymentItem = styled.div`
  box-sizing: border-box;
  width: 34px;
  height: 24px;
  background: #ffffff;
  border: 1px solid #e9eaeb;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img,
  svg {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 24px;
  height: 24px;
  color: #a4a7ae;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  text-decoration: none;

  &:hover {
    color: #717680;
  }
`;

const BottomContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding-top: 32px;
  border-top: 1px solid #e9eaeb;
`;

const Copyright = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #717680;
`;
